/**
 * VISTOERA_SAAS_I18N_FOUNDATION V1 — persistence + first-visit resolution
 * (spec §15/§16/§51).
 *
 * V1 persistence: `localStorage` only (no User/Workspace/Tenant locale
 * field exists in the backend yet — confirmed by the audit, see the final
 * report's AUDIT section). `getInitialUiLocale()` is written as the single
 * seam a future server-side preference would plug into (spec §15's "架构
 * 要为未来 server-side user preference 留接口") — swap step 1 below for a
 * real fetched user preference without touching any call site.
 *
 * Priority order actually implemented, in order (spec §15/§51):
 *   1. Explicit user preference already persisted in `localStorage`.
 *   2. Browser language (`navigator.language`), through a strict allowlist
 *      — but ONLY for a genuinely first-time visitor (no persisted value
 *      at all yet). Spec §51 (P0 "Existing User Compatibility") forbids an
 *      existing user's UI silently flipping to English because of browser
 *      config; since Local/Preview today have zero persisted-locale users
 *      by definition (this key does not exist before this round), every
 *      *current* user is a "new key, first read" case. To stay strictly on
 *      the safe side anyway, first-time-browser-detection is OFF by
 *      default (`ENABLE_BROWSER_LOCALE_DETECTION = false`) — ship the
 *      switcher first, flip this on in a later, explicitly-reported round
 *      once product wants it. See `EXISTING_USER_LOCALE_MIGRATION_STRATEGY`
 *      in the final report.
 *   3. `DEFAULT_UI_LOCALE` (`zh-CN`).
 */
import { DEFAULT_UI_LOCALE, SUPPORTED_UI_LOCALES, type UiLocale, type UiLocaleSource } from './types'

export const UI_LOCALE_STORAGE_KEY = 'vistoera.ui.locale'

/** Flip to `true` only in a dedicated, explicitly-reported round — see the
 * module doc above. Kept as a named, greppable constant rather than a
 * magic boolean inline so a future change is a one-line, obviously-
 * intentional diff. */
export const ENABLE_BROWSER_LOCALE_DETECTION = false

function isSupportedUiLocale(value: string | null | undefined): value is UiLocale {
  return !!value && (SUPPORTED_UI_LOCALES as readonly string[]).includes(value)
}

/** Strict allowlist mapping from `navigator.language`-style tags to a
 * supported `UiLocale` (spec §16 "必须有严格 allowlist"). Never used to
 * build a resource path directly — always looked up through this map. */
function mapBrowserLanguageToUiLocale(browserLanguage: string): UiLocale | null {
  const normalized = browserLanguage.trim().toLowerCase()
  if (normalized === 'zh-cn' || normalized === 'zh' || normalized.startsWith('zh-')) return 'zh-CN'
  if (normalized === 'en-us' || normalized === 'en' || normalized.startsWith('en-')) return 'en-US'
  return null
}

export function readPersistedUiLocale(): UiLocale | null {
  try {
    const raw = window.localStorage.getItem(UI_LOCALE_STORAGE_KEY)
    return isSupportedUiLocale(raw) ? raw : null
  } catch {
    // Storage may be unavailable (private mode / disabled) — never throw
    // out of a locale read; callers fall through to the next priority.
    return null
  }
}

export function persistUiLocale(locale: UiLocale): void {
  try {
    window.localStorage.setItem(UI_LOCALE_STORAGE_KEY, locale)
  } catch {
    // Best-effort — a failed persist just means the choice doesn't survive
    // a refresh; it must never crash the switch itself (spec §19).
  }
}

export function resolveInitialUiLocale(): { locale: UiLocale; source: UiLocaleSource } {
  const persisted = readPersistedUiLocale()
  if (persisted) return { locale: persisted, source: 'persisted' }

  if (ENABLE_BROWSER_LOCALE_DETECTION && typeof navigator !== 'undefined') {
    const detected = mapBrowserLanguageToUiLocale(navigator.language || '')
    if (detected) return { locale: detected, source: 'browser-detected' }
  }

  return { locale: DEFAULT_UI_LOCALE, source: 'default-fallback' }
}
